import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['Amarjeet', 'Axle and FIP', 'F.F. Misc', 'Floors', 'T.assy', 'Yanmar'];
const months = [
  'Jan-25', 'Feb-25', 'Mar-25', 'Apr-25', 'May-25', 'Jun-25',
  'Jul-25', 'Aug-25', 'Sep-25', 'Oct-25', 'Nov-25', 'Dec-25'
];

export default function MonthlySchedules() {
  const [schedules, setSchedules] = useState([]);
  const [balance, setBalance] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [viewMode, setViewMode] = useState('schedules'); // 'schedules' or 'balance'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const [formData, setFormData] = useState({
    partcode: '',
    description: '',
    customer: '',
    category: '',
    monthly_quantity: '',
    month: '',
    required_per_day: '',
    days_booked: ''
  });

  useEffect(() => {
    fetchSchedules();
    fetchCustomers();
  }, [selectedMonth]);

  const fetchSchedules = async () => {
    try {
      const params = selectedMonth ? `?month=${selectedMonth}` : '';
      const response = await axios.get(`${API}/schedules${params}`);
      setSchedules(response.data);
      
      // Also fetch balance
      const balanceResponse = await axios.get(`${API}/schedules/balance${params}`);
      setBalance(balanceResponse.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/schedules`, {
        ...formData,
        monthly_quantity: parseInt(formData.monthly_quantity),
        required_per_day: formData.required_per_day ? parseFloat(formData.required_per_day) : null,
        days_booked: formData.days_booked ? parseInt(formData.days_booked) : null
      });
      setShowAddModal(false);
      resetForm();
      fetchSchedules();
    } catch (error) {
      console.error('Error creating schedule:', error);
      alert('Error creating schedule');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      try {
        await axios.delete(`${API}/schedules/${id}`);
        fetchSchedules();
      } catch (error) {
        console.error('Error deleting schedule:', error);
        alert('Error deleting schedule');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      partcode: '',
      description: '',
      customer: '',
      category: '',
      monthly_quantity: '',
      month: '',
      required_per_day: '',
      days_booked: ''
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if it's an Excel file
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please upload a valid Excel file (.xlsx or .xls)');
        return;
      }
      setUploadFile(file);
    }
  };

  const handleUploadExcel = async () => {
    if (!uploadFile) {
      alert('Please select a file to upload');
      return;
    }

    setUploadLoading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/schedules/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      fetchSchedules();
      
      // Show success message
      alert(`Successfully imported ${response.data.inserted_count} schedules!`);
      
      // Reset and close modal after short delay
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadResult(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error uploading Excel:', error);
      alert(error.response?.data?.detail || 'Error uploading Excel file');
    } finally {
      setUploadLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="monthly-schedules">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Monthly Schedules</h1>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition"
            data-testid="upload-excel-button"
          >
            📊 Upload Excel
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
            data-testid="add-schedule-button"
          >
            + Add Schedule
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 items-center">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
            data-testid="month-filter"
          >
            <option value="">All Months</option>
            {months.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <div className="flex gap-2 ml-auto">
            <button
              onClick={() => setViewMode('schedules')}
              className={`px-4 py-2 rounded-lg transition ${
                viewMode === 'schedules'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              data-testid="view-schedules"
            >
              Schedules
            </button>
            <button
              onClick={() => setViewMode('balance')}
              className={`px-4 py-2 rounded-lg transition ${
                viewMode === 'balance'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              data-testid="view-balance"
            >
              Balance Report
            </button>
          </div>
        </div>
      </div>

      {/* Schedules Table */}
      {viewMode === 'schedules' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partcode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required/Day</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {schedules.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No schedules found. Click "Add Schedule" to create one.
                    </td>
                  </tr>
                ) : (
                  schedules.map((schedule) => (
                    <tr key={schedule.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{schedule.partcode}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{schedule.description}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{schedule.customer}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {schedule.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{schedule.month}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{schedule.monthly_quantity}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{schedule.required_per_day || '-'}</td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleDelete(schedule.id)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`delete-schedule-${schedule.id}`}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Balance Report Table */}
      {viewMode === 'balance' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partcode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dispatched</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {balance.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No balance data available.
                    </td>
                  </tr>
                ) : (
                  balance.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.partcode}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{item.description}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{item.customer}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{item.month}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{item.monthly_quantity}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">{item.dispatched_quantity}</td>
                      <td className="px-6 py-4 text-sm font-semibold">
                        <span className={item.remaining_quantity < 0 ? 'text-red-600' : 'text-blue-600'}>
                          {item.remaining_quantity}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Schedule Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Add Monthly Schedule</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partcode *</label>
                  <input
                    type="text"
                    value={formData.partcode}
                    onChange={(e) => setFormData({ ...formData, partcode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="schedule-partcode-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={formData.customer}
                    onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="schedule-customer-select"
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.name}>{customer.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                  data-testid="schedule-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="schedule-category-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
                  <select
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    data-testid="schedule-month-select"
                  >
                    <option value="">Select Month</option>
                    {months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Quantity *</label>
                  <input
                    type="number"
                    value={formData.monthly_quantity}
                    onChange={(e) => setFormData({ ...formData, monthly_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                    min="1"
                    data-testid="schedule-quantity-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Required per Day</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.required_per_day}
                    onChange={(e) => setFormData({ ...formData, required_per_day: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="schedule-required-per-day-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days Booked</label>
                  <input
                    type="number"
                    value={formData.days_booked}
                    onChange={(e) => setFormData({ ...formData, days_booked: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    data-testid="schedule-days-booked-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  data-testid="cancel-schedule-button"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  data-testid="submit-schedule-button"
                >
                  Add Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Upload Excel Schedule</h2>
              <p className="text-sm text-gray-600 mt-2">
                Upload an Excel file with your monthly schedules
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">📋 Excel Format Required:</h3>
                <p className="text-sm text-blue-800 mb-2">Your Excel file must have these columns:</p>
                <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                  <li><strong>partcode</strong> - Part code (e.g., 10022487BE)</li>
                  <li><strong>description</strong> - Part description</li>
                  <li><strong>customer</strong> - Customer name (e.g., ITL)</li>
                  <li><strong>category</strong> - Category (Amarjeet, Axle and FIP, etc.)</li>
                  <li><strong>monthly_quantity</strong> - Monthly quantity (number)</li>
                  <li><strong>month</strong> - Month (e.g., Dec-25)</li>
                  <li><strong>required_per_day</strong> - Required per day (optional)</li>
                  <li><strong>days_booked</strong> - Days booked (optional)</li>
                </ul>
              </div>

              {/* File Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Excel File (.xlsx or .xls)
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="excel-file-input"
                />
                {uploadFile && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Selected: {uploadFile.name}
                  </p>
                )}
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-semibold">
                    ✅ {uploadResult.message}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Imported: {uploadResult.inserted_count} / {uploadResult.total_rows} rows
                  </p>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-orange-700 font-medium">Errors:</p>
                      <ul className="text-xs text-orange-600 list-disc list-inside mt-1">
                        {uploadResult.errors.slice(0, 5).map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadResult(null);
                  }}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  data-testid="cancel-upload-button"
                  disabled={uploadLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUploadExcel}
                  disabled={!uploadFile || uploadLoading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-upload-button"
                >
                  {uploadLoading ? 'Uploading...' : 'Upload & Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
